import { Stack, Typography } from '@mui/material';
import EurCurrencyField from '@/components/form/EurCurrencyField.tsx';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';

interface Props {
  formData: OpportunityFormData;
  handleChangeField: (field: string, value: string | number | null) => void;
}

const OpportunityMonthlyRentSection = ({ formData, handleChangeField }: Props) => {
  const renderTraditionalRentFields = () => (
    <>
      <EurCurrencyField
        id="expectedMonthlyRent"
        name="expectedMonthlyRent"
        label="¿Alquiler mensual esperado?"
        value={formData.expectedMonthlyRent}
        onChange={(value) => handleChangeField('expectedMonthlyRent', value)}
        allowDecimalScale={false}
      />
    </>
  );

  const renderRoomRentFields = () => (
    <>
      <EurCurrencyField
        id="roomRent"
        name="roomRent"
        label="¿Precio medio habitación?"
        value={formData.roomRent}
        onChange={(value) => handleChangeField('roomRent', value)}
        allowDecimalScale={false}
      />
      <EurCurrencyField
        id="totalRoomRent"
        name="totalRoomRent"
        label={`Total por ${formData.rooms} habitaciones`}
        disabled={true}
        value={formData.roomRent && formData.rooms ? formData.roomRent * formData.rooms : null}
        allowDecimalScale={false}
      />
    </>
  );

  const renderRoomSaleFields = () => (
    <>
      <EurCurrencyField
        id="expectedMonthlyRent"
        name="expectedMonthlyRent"
        label="¿Precio de venta de la habitación?"
        value={formData.expectedMonthlyRent}
        onChange={(value) => handleChangeField('expectedMonthlyRent', value)}
        allowDecimalScale={false}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Introduce el precio de venta esperado para esta habitación individual.
      </Typography>
    </>
  );

  const renderVacationalFields = () => (
    // TODO: Add specific fields for vacational rentals
    <>
      <EurCurrencyField
        id="expectedMonthlyRent"
        name="expectedMonthlyRent"
        label="¿Ingresos mensuales esperados?"
        value={formData.expectedMonthlyRent}
        onChange={(value) => handleChangeField('expectedMonthlyRent', value)}
        allowDecimalScale={false}
      />
    </>
  );

  const renderFlippingFields = () => (
    // TODO: Add specific fields for flipping
    <>
      <Typography color="text.secondary">
        Para proyectos de Flipping, los ingresos se calculan basándose en la venta futura de la
        propiedad.
      </Typography>
    </>
  );

  const renderChangeOfUseFields = () => (
    // TODO: Add specific fields for change of use
    <>
      <EurCurrencyField
        id="expectedMonthlyRent"
        name="expectedMonthlyRent"
        label="¿Ingresos mensuales esperados? (cambio de uso)"
        value={formData.expectedMonthlyRent}
        onChange={(value) => handleChangeField('expectedMonthlyRent', value)}
        allowDecimalScale={false}
      />
    </>
  );

  const renderCommonExpenseFields = () => (
    <>
      <EurCurrencyField
        id="communityFee"
        name="communityFee"
        label="Cuota comunidad (mensual)"
        value={formData.communityFee}
        onChange={(value) => handleChangeField('communityFee', value)}
        allowDecimalScale={false}
      />
      <Stack spacing={1}>
        <EurCurrencyField
          id="propertyTaxIbi"
          name="propertyTaxIbi"
          label="Importe IBI (anual)"
          value={formData.propertyTaxIbi}
          onChange={(value) => handleChangeField('propertyTaxIbi', value)}
          allowDecimalScale={false}
        />
        <Typography variant="caption" color="text.secondary">
          (El impuesto de bienes inmuebles se paga una vez al año. Corresponde al vendedor en el año
          en curso, pero muchas veces se prorratea. Comprobar que están al día los pagos)
        </Typography>
      </Stack>
      <Stack spacing={1}>
        <EurCurrencyField
          id="wasteTax"
          name="wasteTax"
          label="Tasa de basuras (anual)"
          value={formData.wasteTax}
          onChange={(value) => handleChangeField('wasteTax', value)}
          allowDecimalScale={false}
        />
        <Typography variant="caption" color="text.secondary">
          (a veces está incluida en el recibo del agua)
        </Typography>
      </Stack>
      <EurCurrencyField
        id="homeInsurance"
        name="homeInsurance"
        label="Seguro hogar (anual)"
        value={formData.homeInsurance}
        onChange={(value) => handleChangeField('homeInsurance', value)}
        allowDecimalScale={false}
      />
    </>
  );

  const renderRentSpecificFields = () => (
    <>
      <EurCurrencyField
        id="rentDefaultInsurance"
        name="rentDefaultInsurance"
        label="Seguro de impagos (anual)"
        value={formData.rentDefaultInsurance}
        onChange={(value) => handleChangeField('rentDefaultInsurance', value)}
        allowDecimalScale={false}
      />
    </>
  );

  return (
    <Stack spacing={3}>
      {/* Render income fields based on investment type */}
      {formData.type === 'TRADITIONAL_RENT' && renderTraditionalRentFields()}
      {formData.type === 'ROOM_RENT' && renderRoomRentFields()}
      {formData.type === 'ROOM_SALE' && renderRoomSaleFields()}
      {formData.type === 'VACATIONAL' && renderVacationalFields()}
      {formData.type === 'FLIPPING' && renderFlippingFields()}
      {formData.type === 'CHANGE_OF_USE' && renderChangeOfUseFields()}

      {/* Common expense fields for all types except flipping and room sale */}
      {formData.type !== 'FLIPPING' && formData.type !== 'ROOM_SALE' && renderCommonExpenseFields()}

      {/* Rent-specific insurance only for rental types */}
      {(formData.type === 'TRADITIONAL_RENT' ||
        formData.type === 'ROOM_RENT' ||
        formData.type === 'VACATIONAL') &&
        renderRentSpecificFields()}

      <EurCurrencyField
        id="otherExpenses"
        name="otherExpenses"
        label="Otros gastos (anuales)"
        value={formData.otherExpenses}
        onChange={(value) => handleChangeField('otherExpenses', value)}
        allowDecimalScale={false}
      />
    </Stack>
  );
};

export default OpportunityMonthlyRentSection;
